/* =====================================================================
 * WorkspaceModeContent — concrete page renderer for workspace modes.
 *
 * B2C and enterprise hosts supply their feature flags and page slots.
 * ===================================================================== */

import React, { Suspense, lazy } from 'react';
import { LiveAgentDock } from '../modes/live-agent/LiveAgentDock';
import DocumentModePage from '../modes/document/DocumentModePage';

import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Construction } from 'lucide-react';
import { useChatStore } from '@auw/stores';
import { WORKSPACE_MODES, type WorkspaceMode } from '@auw/workspace-utils';
import { useFeatureFlags, type FeatureId } from '@auw/feature-flags';
import { WorkspaceModeFrame } from '../workspace/shell/WorkspaceModeFrame';
import { WorkspaceSplitPane } from '../workspace/panes/WorkspaceSplitPane';
import { AgentModePage } from '../modes/agent/AgentModePage';
import {
  getDocAgentEditorPageComponent,
  getDocAgentLandingPageComponent,
  getDocAgentModePageComponent,
} from '../modes/doc-agent/docAgentSlot';
import { getGtcAgentModePageComponent } from '../modes/gtc-agent/gtcAgentSlot';
import { getPurchaseAgentModePageComponent } from '../modes/purchase-agent/purchaseAgentSlot';
import { getMemoryModePageComponent } from '../modes/memory/memorySlot';
import { getTranslationModePageComponent } from '../modes/translation/translationSlot';
import { useWorkspaceRoute } from '../workspace/navigation/useWorkspaceRoute';

const PluginPage = lazy(() => import('@auw/workspace-ui').then((m) => ({ default: m.PluginPage })));
const DashboardPage = lazy(() => import('@auw/workspace-ui').then((m) => ({ default: m.DashboardPage })));
const AdminDashboardPage = lazy(() => import('@auw/workspace-ui').then((m) => ({ default: m.AdminDashboardPage })));
const StoragePage = lazy(() => import('@auw/workspace-ui').then((m) => ({ default: m.StoragePage })));
const UserGuidePage = lazy(() => import('@auw/workspace-ui').then((m) => ({ default: m.UserGuidePage })));
const SkillMarketplacePage = lazy(() => import('@auw/workspace-ui').then((m) => ({ default: m.SkillMarketplacePage })));

const LiveAgentPage = lazy(() =>
  import('../modes/live-agent/LiveAgentPage').then((m) => ({ default: m.LiveAgentPage })),
);

const DMPage = lazy(() => import('@auw/workspace-ui').then((m) => ({ default: m.DMPage })));

const AppManagePage = lazy(() => import('@auw/workspace-ui').then((m) => ({ default: m.AppManagePage })));

const AdminPage = lazy(() => import('@auw/workspace-ui').then((m) => ({ default: m.AdminPage })));

const SkillCreatePage = lazy(() => import('@auw/workspace-ui').then((m) => ({ default: m.SkillCreatePage })));

const WorkspaceModeStartPage = lazy(() =>
  import('../workspace/shell/WorkspaceModeStartPage').then((m) => ({ default: m.WorkspaceModeStartPage })),
);

const WidgetPage = lazy(() => import('@auw/workspace-ui').then((m) => ({ default: m.WidgetPage })));

const FeedPage = lazy(() => import('@auw/workspace-ui').then((m) => ({ default: m.FeedPage })));

const ToolsPage = lazy(() => import('@auw/workspace-ui').then((m) => ({ default: m.ToolsPage })));

const PlaceholderWrap = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const PlaceholderIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
`;

const PlaceholderLabel = styled.span`
  font-size: 1rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const PlaceholderSub = styled.span`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const ContentFallback = styled.div`
  flex: 1;
  min-height: 0;
`;

const PlaceholderPage: React.FC<{
  mode: WorkspaceMode;
  /** 기본 문구("준비 중입니다") 대신 쓸 common 네임스페이스 키. */
  subLabelKey?: string;
}> = ({ mode, subLabelKey }) => {
  const { t } = useTranslation();
  const info = WORKSPACE_MODES[mode];
  const ModeIcon = info?.icon ?? Construction;
  return (
    <PlaceholderWrap>
      <PlaceholderIcon>
        <ModeIcon size={32} />
      </PlaceholderIcon>
      <PlaceholderLabel>{info ? t(info.labelKey) : mode}</PlaceholderLabel>
      <PlaceholderSub>{t(subLabelKey ?? 'common:chat.preparing')}</PlaceholderSub>
    </PlaceholderWrap>
  );
};

export interface WorkspaceModeContentProps {
  readonly mode?: WorkspaceMode;
  readonly inputSlot?: React.ReactNode;
  readonly docAgentView?: 'auto' | 'landing' | 'editor';
}

export const WorkspaceModeContent: React.FC<WorkspaceModeContentProps> = ({
  mode,
  inputSlot,
  docAgentView = 'auto',
}) => {
  const activeModeFallback = useChatStore((s) => s.activeMode);
  const route = useWorkspaceRoute();
  const activeMode = mode ?? route.mode ?? activeModeFallback;
  const { isEnabled: isFeatureEnabled } = useFeatureFlags();
  const modeFeatureId = `mode.${activeMode}` as FeatureId;
  const isActiveModeEnabled = isFeatureEnabled(modeFeatureId);

  const activeConversation = useChatStore((state) => state.activeConversation());
  const showStartPage = !activeConversation || activeConversation.startPagePending === true;

  const renderContent = () => {
    if (!isActiveModeEnabled) {
      return (
        <WorkspaceModeFrame mode={activeMode} inputSlot={inputSlot}>
          <PlaceholderPage mode={activeMode} />
        </WorkspaceModeFrame>
      );
    }

    switch (activeMode) {
      case 'agent':
        return (
          <WorkspaceModeFrame mode="agent" inputSlot={inputSlot}>
            <AgentModePage />
          </WorkspaceModeFrame>
        );

      case 'doc-agent': {
        if (docAgentView === 'landing') {
          const DocAgentLandingPage = getDocAgentLandingPageComponent();
          if (DocAgentLandingPage) return <DocAgentLandingPage />;
        }

        if (docAgentView === 'editor') {
          const DocAgentEditorPage = getDocAgentEditorPageComponent();
          if (DocAgentEditorPage && route.itemId) {
            return <DocAgentEditorPage itemId={route.itemId} />;
          }
        }

        const DocAgentModePage = getDocAgentModePageComponent();
        if (DocAgentModePage) return <DocAgentModePage />;
        return (
          <WorkspaceModeFrame mode="doc-agent" inputSlot={inputSlot}>
            <PlaceholderPage mode="doc-agent" />
          </WorkspaceModeFrame>
        );
      }

      case 'gtc-agent': {
        const GtcAgentModePage = getGtcAgentModePageComponent();
        return (
          <WorkspaceModeFrame mode="gtc-agent" inputSlot={inputSlot}>
            {GtcAgentModePage ? <GtcAgentModePage /> : <PlaceholderPage mode="gtc-agent" />}
          </WorkspaceModeFrame>
        );
      }

      /* 구매 부서 에이전트 — 선적서류 추출 검토 작업대.
       * gtc-agent 와 같은 host 주입 슬롯 패턴 (업로드·PDF 렌더·XLSX 직렬화가
       * host 의존). 슬롯 미주입 빌드에서는 통합 채팅 UI 로 폴백한다. */
      case 'purchase-agent': {
        const PurchaseAgentModePage = getPurchaseAgentModePageComponent();
        return (
          <WorkspaceModeFrame mode="purchase-agent" inputSlot={inputSlot}>
            {PurchaseAgentModePage ? <PurchaseAgentModePage /> : <AgentModePage />}
          </WorkspaceModeFrame>
        );
      }

      /* 공문 에이전트 — 전용 화면이 아직 없다. `default:` 로 흘리면
       * WorkspaceModeFrame 의 목록 패널까지 딸려 와 "빈 대화 목록이 있는
       * 미완성 화면" 처럼 보이므로, 안내 문구만 단독으로 그린다. 사이드바를
       * 다시 펼치는 버튼은 registry 의 `showTopBar: true` 가 담당한다. */
      case 'official-doc-agent':
        return (
          <PlaceholderPage
            mode="official-doc-agent"
            subLabelKey="common:workspace.featurePreparing"
          />
        );

      /* 대화 이력이 없는 단발성 도구라 WorkspaceModeFrame(=목록 패널 +
       * 워크스페이스 헤더) 없이 페이지를 그대로 건다 — storage / plugin /
       * dashboard 와 같은 취급. 메인 사이드바 펼치기 버튼은 페이지가 자체
       * 헤더에 그린다. */
      case 'translation': {
        const TranslationModePage = getTranslationModePageComponent();
        return TranslationModePage
          ? <TranslationModePage />
          : <PlaceholderPage mode="translation" />;
      }

      /* 에이전트 메모리 — translation 과 같은 frameless 단발성 페이지. */
      case 'memory': {
        const MemoryModePage = getMemoryModePageComponent();
        return MemoryModePage
          ? <MemoryModePage />
          : <PlaceholderPage mode="memory" />;
      }

      case 'storage':
        return <StoragePage />;

      case 'plugin':
        return <PluginPage />;

      case 'dashboard':
        return <DashboardPage />;

      case 'admin-dashboard':
        return <AdminDashboardPage />;

      /* 사용자 가이드 — 좌측 목차 패널을 직접 그리는 frameless 페이지.
       * WorkspaceModeFrame 의 대화 목록 패널이 붙으면 패널이 둘이 된다. */
      case 'user-guide':
        return <UserGuidePage />;

      /* 에이전트 스킬 마켓플레이스 — dashboard 와 같은 frameless 페이지.
       * 페이지가 자체 헤더(메인 사이드바 펼치기 버튼 포함)를 그린다. */
      case 'skill-marketplace':
        return <SkillMarketplacePage />;

      case 'live-agent':
        return (
          <WorkspaceModeFrame mode="live-agent" aboveInputSlot={<LiveAgentDock />}>
            <LiveAgentPage />
          </WorkspaceModeFrame>
        );

      case 'document':
        return (
          <WorkspaceModeFrame mode="document" inputSlot={inputSlot}>
            {showStartPage ? (
              <WorkspaceModeStartPage mode="document" />
            ) : (
              <DocumentModePage />
            )}
          </WorkspaceModeFrame>
        );

      case 'message':
        return <DMPage />;

      case 'widget':
        return <WidgetPage />;

      case 'feed':
        return <FeedPage />;

      case 'tools':
        return <ToolsPage />;

      case 'app-manage':
        return <AppManagePage />;

      case 'admin-page':
        return <AdminPage />;

      case 'skill-create':
        return <SkillCreatePage />;

      default:
        return (
          <WorkspaceModeFrame mode={activeMode} inputSlot={inputSlot}>
            <WorkspaceSplitPane
              primaryContent={<PlaceholderPage mode={activeMode} />}
            />
          </WorkspaceModeFrame>
        );
    }
  };

  return (
    <Suspense fallback={<ContentFallback />}>
      {renderContent()}
    </Suspense>
  );
};
